// "Aguardando confirmação" de bloqueio/desbloqueio, compartilhado entre
// popup de comandos, painel do mapa e PWA (pedido 2026-09-24: J4 só
// bloqueia por SMS e a tela tem que ficar aguardando ATÉ o rastreador
// reportar o bloqueio — não só 45s). Guardado no localStorage pra
// sobreviver a F5/troca de tela. NUNCA é a fonte do estado de bloqueio —
// quem manda continua sendo v.blocked (Traccar); isto só marca "comando
// saiu e ainda não foi confirmado".
import { useEffect, useSyncExternalStore } from "react";

export interface PendingBlockCommand {
  action: "block" | "unblock";
  channel: "gprs" | "sms";
  sentAt: number;
  // null = sem prazo: espera até o rastreador confirmar (equipamento só-SMS).
  expiresAt: number | null;
}

const STORAGE_KEY = "gpsdrive.pending-block-commands";
// Mesmo só-SMS, nunca prende a tela pra sempre se o rastreador sumir.
const SMS_ONLY_MAX_WAIT_MS = 24 * 60 * 60 * 1000;

type State = Record<string, PendingBlockCommand>;
let state: State = {};
try {
  state = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") as State;
} catch {
  state = {};
}
const listeners = new Set<() => void>();

function commit(next: State) {
  state = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // modo privado/quota — só perde a persistência entre F5s
  }
  listeners.forEach((l) => l());
}

export function setPendingBlockCommand(
  vehicleId: string,
  action: "block" | "unblock",
  channel: "gprs" | "sms",
  waitMs: number | null,
) {
  const now = Date.now();
  commit({
    ...state,
    [vehicleId]: { action, channel, sentAt: now, expiresAt: now + (waitMs ?? SMS_ONLY_MAX_WAIT_MS) },
  });
}

export function clearPendingBlockCommand(vehicleId: string) {
  if (!state[vehicleId]) return;
  const { [vehicleId]: _removed, ...rest } = state;
  commit(rest);
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

// Estado pendente do veículo; some sozinho quando `blocked` (vindo do
// Traccar) bate com o que foi pedido, ou quando o prazo acaba.
export function usePendingBlockCommand(
  vehicleId: string | undefined,
  blocked: boolean | undefined,
  onExpire?: (pending: PendingBlockCommand) => void,
): PendingBlockCommand | null {
  const all = useSyncExternalStore(subscribe, () => state, () => state);
  const pending = vehicleId ? (all[vehicleId] ?? null) : null;

  useEffect(() => {
    if (!vehicleId || !pending || blocked === undefined) return;
    if ((pending.action === "block") === blocked) clearPendingBlockCommand(vehicleId);
  }, [vehicleId, pending, blocked]);

  useEffect(() => {
    if (!vehicleId || !pending?.expiresAt) return;
    const ms = pending.expiresAt - Date.now();
    const expire = () => {
      clearPendingBlockCommand(vehicleId);
      onExpire?.(pending);
    };
    if (ms <= 0) {
      expire();
      return;
    }
    const t = setTimeout(expire, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, pending]);

  return pending;
}
